import { TechRadarApi } from '@backstage-community/plugin-tech-radar';
import {
  TechRadarLoaderResponse,
  MovedState,
} from '@backstage-community/plugin-tech-radar-common';

export class MyTechRadarClient implements TechRadarApi {
  constructor(private readonly dataUrl: string = '/tech-radar-data.json') {}

  private withCacheBuster(url: string): string {
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}_ts=${Date.now()}`;
  }

  private normalizeResponseDates(
    raw: TechRadarLoaderResponse,
  ): TechRadarLoaderResponse {
    const entries = (raw.entries ?? []).map(entry => ({
      ...entry,
      timeline: (entry.timeline ?? []).map(item => ({
        ...item,
        date: new Date(item.date as unknown as string),
      })),
    }));

    return {
      ...raw,
      entries,
    } as TechRadarLoaderResponse;
  }

  private async loadRemote(): Promise<TechRadarLoaderResponse | undefined> {
    try {
      const response = await fetch(this.withCacheBuster(this.dataUrl), {
        cache: 'no-store',
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) {
        return undefined;
      }

      const data = (await response.json()) as TechRadarLoaderResponse;
      return this.normalizeResponseDates(data);
    } catch {
      return undefined;
    }
  }

  async load(_id?: string): Promise<TechRadarLoaderResponse> {
    const fallbackData: TechRadarLoaderResponse = {
      title: 'Technology Radar',
      quadrants: [
        { id: '1', name: 'Languages & Frameworks' },
        { id: '2', name: 'Tools' },
        { id: '3', name: 'Platforms' },
        { id: '4', name: 'Techniques' },
      ],
      rings: [
        { id: 'adopt', name: 'ADOPT', color: '#5BA300' },
        { id: 'trial', name: 'TRIAL', color: '#009EB0' },
        { id: 'assess', name: 'ASSESS', color: '#F5A623' },
        { id: 'hold', name: 'HOLD', color: '#C73122' },
      ],
      entries: [
        // Languages & Frameworks (Quadrant 1)
        {
          key: 'typescript',
          id: 'typescript',
          quadrant: '1',
          title: 'TypeScript',
          description:
            'A typed superset of JavaScript that compiles to plain JavaScript. Essential for type-safe development.',
          url: 'https://www.typescriptlang.org/',
          timeline: [
            {
              moved: MovedState.Up,
              ringId: 'adopt',
              date: new Date('2024-01-01'),
              description:
                'Promoted to ADOPT - now our standard for all frontend and backend development',
            },
          ],
        },
        {
          key: 'react',
          id: 'react',
          quadrant: '1',
          title: 'React',
          description:
            'JavaScript library for building user interfaces with component-based architecture.',
          url: 'https://react.dev/',
          timeline: [
            {
              moved: MovedState.NoChange,
              ringId: 'adopt',
              date: new Date('2023-06-01'),
              description: 'Established as our primary frontend framework',
            },
          ],
        },
        {
          key: 'nextjs',
          id: 'nextjs',
          quadrant: '1',
          title: 'Next.js',
          description:
            'React framework with hybrid static & server rendering, TypeScript support, and smart bundling.',
          url: 'https://nextjs.org/',
          timeline: [
            {
              moved: MovedState.Up,
              ringId: 'trial',
              date: new Date('2025-09-01'),
              description:
                'Under evaluation for new projects requiring SSR capabilities',
            },
          ],
        },
        {
          key: 'python',
          id: 'python',
          quadrant: '1',
          title: 'Python',
          description:
            'High-level programming language widely used for backend services, data processing, and ML.',
          url: 'https://www.python.org/',
          timeline: [
            {
              moved: MovedState.NoChange,
              ringId: 'adopt',
              date: new Date('2023-03-01'),
              description: 'Standard for backend services and data pipelines',
            },
          ],
        },
        {
          key: 'golang',
          id: 'golang',
          quadrant: '1',
          title: 'Go',
          description:
            'Statically typed, compiled language designed for simplicity and efficiency in concurrent systems.',
          url: 'https://go.dev/',
          timeline: [
            {
              moved: MovedState.Up,
              ringId: 'trial',
              date: new Date('2025-06-01'),
              description: 'Being evaluated for high-performance microservices',
            },
          ],
        },
        {
          key: 'rust',
          id: 'rust',
          quadrant: '1',
          title: 'Rust',
          description:
            'Systems programming language focused on safety, concurrency, and performance.',
          url: 'https://www.rust-lang.org/',
          timeline: [
            {
              moved: MovedState.Up,
              ringId: 'assess',
              date: new Date('2025-11-01'),
              description: 'Exploring for performance-critical components',
            },
          ],
        },
        {
          key: 'angular',
          id: 'angular',
          quadrant: '1',
          title: 'Angular',
          description:
            'Platform for building mobile and desktop web applications.',
          url: 'https://angular.io/',
          timeline: [
            {
              moved: MovedState.Down,
              ringId: 'hold',
              date: new Date('2024-08-01'),
              description: 'Phasing out in favor of React for new projects',
            },
          ],
        },

        // Tools (Quadrant 2)
        {
          key: 'github-actions',
          id: 'github-actions',
          quadrant: '2',
          title: 'GitHub Actions',
          description:
            'CI/CD platform that allows you to automate build, test, and deployment pipelines.',
          url: 'https://github.com/features/actions',
          timeline: [
            {
              moved: MovedState.NoChange,
              ringId: 'adopt',
              date: new Date('2023-05-01'),
              description: 'Primary CI/CD solution for all repositories',
            },
          ],
        },
        {
          key: 'docker',
          id: 'docker',
          quadrant: '2',
          title: 'Docker',
          description:
            'Platform for developing, shipping, and running applications in containers.',
          url: 'https://www.docker.com/',
          timeline: [
            {
              moved: MovedState.NoChange,
              ringId: 'adopt',
              date: new Date('2023-01-01'),
              description: 'Standard containerization solution',
            },
          ],
        },
        {
          key: 'terraform',
          id: 'terraform',
          quadrant: '2',
          title: 'Terraform',
          description:
            'Infrastructure as Code tool for building, changing, and versioning infrastructure safely.',
          url: 'https://www.terraform.io/',
          timeline: [
            {
              moved: MovedState.Up,
              ringId: 'adopt',
              date: new Date('2024-03-01'),
              description: 'Standardized for all infrastructure provisioning',
            },
          ],
        },
        {
          key: 'backstage',
          id: 'backstage',
          quadrant: '2',
          title: 'Backstage',
          description:
            'Open platform for building developer portals - unifies tools, services, and documentation.',
          url: 'https://backstage.io/',
          timeline: [
            {
              moved: MovedState.Up,
              ringId: 'trial',
              date: new Date('2025-10-01'),
              description: 'In active use for developer portal',
            },
          ],
        },
        {
          key: 'grafana',
          id: 'grafana',
          quadrant: '2',
          title: 'Grafana',
          description:
            'Open source analytics and interactive visualization web application.',
          url: 'https://grafana.com/',
          timeline: [
            {
              moved: MovedState.NoChange,
              ringId: 'adopt',
              date: new Date('2023-07-01'),
              description: 'Primary monitoring and observability dashboards',
            },
          ],
        },
        {
          key: 'jest',
          id: 'jest',
          quadrant: '2',
          title: 'Jest',
          description:
            'JavaScript testing framework with a focus on simplicity.',
          url: 'https://jestjs.io/',
          timeline: [
            {
              moved: MovedState.NoChange,
              ringId: 'adopt',
              date: new Date('2023-04-01'),
              description:
                'Standard testing framework for JavaScript/TypeScript',
            },
          ],
        },
        {
          key: 'playwright',
          id: 'playwright',
          quadrant: '2',
          title: 'Playwright',
          description:
            'Framework for Web Testing and Automation across Chromium, Firefox and WebKit.',
          url: 'https://playwright.dev/',
          timeline: [
            {
              moved: MovedState.Up,
              ringId: 'trial',
              date: new Date('2025-08-01'),
              description: 'Evaluating for E2E testing',
            },
          ],
        },
        {
          key: 'jenkins',
          id: 'jenkins',
          quadrant: '2',
          title: 'Jenkins',
          description:
            'Open source automation server for building, testing, and deploying software.',
          url: 'https://www.jenkins.io/',
          timeline: [
            {
              moved: MovedState.Down,
              ringId: 'hold',
              date: new Date('2024-06-01'),
              description: 'Migrating to GitHub Actions',
            },
          ],
        },

        // Platforms (Quadrant 3)
        {
          key: 'kubernetes',
          id: 'kubernetes',
          quadrant: '3',
          title: 'Kubernetes',
          description:
            'Production-grade container orchestration system for automating deployment, scaling, and management.',
          url: 'https://kubernetes.io/',
          timeline: [
            {
              moved: MovedState.NoChange,
              ringId: 'adopt',
              date: new Date('2023-02-01'),
              description: 'Primary container orchestration platform',
            },
          ],
        },
        {
          key: 'aws',
          id: 'aws',
          quadrant: '3',
          title: 'AWS',
          description:
            'Amazon Web Services - comprehensive cloud computing platform.',
          url: 'https://aws.amazon.com/',
          timeline: [
            {
              moved: MovedState.NoChange,
              ringId: 'adopt',
              date: new Date('2022-12-01'),
              description: 'Primary cloud provider',
            },
          ],
        },
        {
          key: 'postgresql',
          id: 'postgresql',
          quadrant: '3',
          title: 'PostgreSQL',
          description:
            'Advanced open source relational database with strong ACID compliance.',
          url: 'https://www.postgresql.org/',
          timeline: [
            {
              moved: MovedState.NoChange,
              ringId: 'adopt',
              date: new Date('2023-01-01'),
              description: 'Standard relational database',
            },
          ],
        },
        {
          key: 'redis',
          id: 'redis',
          quadrant: '3',
          title: 'Redis',
          description:
            'In-memory data structure store used as database, cache, and message broker.',
          url: 'https://redis.io/',
          timeline: [
            {
              moved: MovedState.NoChange,
              ringId: 'adopt',
              date: new Date('2023-03-01'),
              description: 'Standard caching and session management solution',
            },
          ],
        },
        {
          key: 'gcp',
          id: 'gcp',
          quadrant: '3',
          title: 'Google Cloud Platform',
          description:
            'Suite of cloud computing services running on Google infrastructure.',
          url: 'https://cloud.google.com/',
          timeline: [
            {
              moved: MovedState.Up,
              ringId: 'assess',
              date: new Date('2025-10-01'),
              description: 'Evaluating for multi-cloud strategy',
            },
          ],
        },
        {
          key: 'mongodb',
          id: 'mongodb',
          quadrant: '3',
          title: 'MongoDB',
          description: 'Document-oriented NoSQL database program.',
          url: 'https://www.mongodb.com/',
          timeline: [
            {
              moved: MovedState.Down,
              ringId: 'hold',
              date: new Date('2024-09-01'),
              description: 'Consolidating on PostgreSQL with JSONB support',
            },
          ],
        },

        // Techniques (Quadrant 4)
        {
          key: 'microservices',
          id: 'microservices',
          quadrant: '4',
          title: 'Microservices Architecture',
          description:
            'Architectural style that structures an application as a collection of loosely coupled services.',
          url: '#',
          timeline: [
            {
              moved: MovedState.NoChange,
              ringId: 'adopt',
              date: new Date('2023-01-01'),
              description: 'Standard architecture pattern for new services',
            },
          ],
        },
        {
          key: 'gitops',
          id: 'gitops',
          quadrant: '4',
          title: 'GitOps',
          description:
            'Operational framework that takes DevOps best practices used for application development and applies them to infrastructure automation.',
          url: '#',
          timeline: [
            {
              moved: MovedState.Up,
              ringId: 'adopt',
              date: new Date('2024-05-01'),
              description: 'Standard deployment methodology',
            },
          ],
        },
        {
          key: 'api-first',
          id: 'api-first',
          quadrant: '4',
          title: 'API-First Design',
          description:
            'Treating APIs as first-class citizens and designing them before implementing the underlying services.',
          url: '#',
          timeline: [
            {
              moved: MovedState.NoChange,
              ringId: 'adopt',
              date: new Date('2023-09-01'),
              description: 'Standard approach for new service development',
            },
          ],
        },
        {
          key: 'tdd',
          id: 'tdd',
          quadrant: '4',
          title: 'Test-Driven Development',
          description:
            'Software development approach where tests are written before the code that needs to be tested.',
          url: '#',
          timeline: [
            {
              moved: MovedState.NoChange,
              ringId: 'trial',
              date: new Date('2024-02-01'),
              description: 'Encouraged for critical business logic',
            },
          ],
        },
        {
          key: 'event-sourcing',
          id: 'event-sourcing',
          quadrant: '4',
          title: 'Event Sourcing',
          description:
            'Pattern where state changes are logged as a time-ordered sequence of records.',
          url: '#',
          timeline: [
            {
              moved: MovedState.Up,
              ringId: 'assess',
              date: new Date('2025-07-01'),
              description: 'Exploring for audit-heavy domains',
            },
          ],
        },
        {
          key: 'monorepo',
          id: 'monorepo',
          quadrant: '4',
          title: 'Monorepo',
          description:
            'Software development strategy where code for many projects is stored in the same repository.',
          url: '#',
          timeline: [
            {
              moved: MovedState.Up,
              ringId: 'trial',
              date: new Date('2025-04-01'),
              description: 'Testing with select teams',
            },
          ],
        },
        {
          key: 'microfront',
          id: 'microfront',
          quadrant: '4',
          title: 'Micro Frontends',
          description:
            'Architectural style where frontend apps are decomposed into individual, semi-independent "micro" apps.',
          url: '#',
          timeline: [
            {
              moved: MovedState.Down,
              ringId: 'hold',
              date: new Date('2024-11-01'),
              description: 'Added complexity not justified for our use cases',
            },
          ],
        },
      ],
    } as unknown as TechRadarLoaderResponse;

    const remoteData = await this.loadRemote();
    return remoteData ?? fallbackData;
  }
}
